import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Button, { type ButtonProps } from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { recordPage } from "permissions/_views/wording/permission-wording";

/** An outlined Material button's top padding, by size: one pixel less than a text button's, for its border. */
const OUTLINED_TOP_PADDING: Record<NonNullable<ButtonProps["size"]>, string> = { small: "3px", medium: "5px", large: "7px" };

/**
 * Who has access, as an outlined button with the lock every record shows for it (#6598), for a screen
 * whose header or actions are a row of text buttons: a template's page, a calendar event's details, an
 * organization agreement (#6839). A proposal and a work agreement draw `RecordAccessButton` instead,
 * beside Comments and Activity, because their headers are icons with captions.
 *
 * `iconOnly` draws the lock alone, named and with a tooltip, for a header whose actions already fill
 * the row: a template's page, where a fifth button with a label took the room its title had beside
 * them.
 *
 * The caller draws it only when `useWhoHasAccess` says the reader may open it, and passes that hook's
 * `open` for the record on the screen.
 */
export default function WhoHasAccessButton(
  props: Readonly<{ onClick: () => void; size?: ButtonProps["size"]; iconOnly?: boolean }>,
) {
  const size = props.size ?? "medium";
  if (!props.iconOnly) {
    return (
      <Button variant="outlined" color="primary" size={size} startIcon={<LockOutlinedIcon />} onClick={props.onClick}>
        {recordPage.whoHasAccess}
      </Button>
    );
  }
  return (
    <Tooltip title={recordPage.whoHasAccess}>
      <Button
        variant="outlined"
        color="primary"
        size={size}
        aria-label={recordPage.whoHasAccess}
        onClick={props.onClick}
        // Square, and as tall as the text buttons beside it: an outlined button's side padding set to its
        // top padding. Doubled, so it outranks a header's own rule for its buttons' side padding.
        sx={{ minWidth: 0, "&&": { paddingInline: OUTLINED_TOP_PADDING[size] } }}
      >
        <LockOutlinedIcon />
      </Button>
    </Tooltip>
  );
}
